// ============================================================================
// PANEL LATERAL DEL EMBUDO — el chat al costado (T-LAT-3 · R-LAT-01/02/03).
// Clic en una tarjeta ⇒ sale de ella el hilo COMPLETO (el mismo `Hilo` del
// inbox) más la `Ficha360`, y al cerrar se mete de vuelta en la tarjeta.
// Lo pidió Cristhian: «el trabajo se haga desde el embudo».
//
// ⛔⛔ TRES COSAS QUE NO SE TOCAN AQUÍ:
//
// 1) ENVIAR NO SE COPIA (I-6). La lógica que decide si el bot se calla vive en
//    `responder.ts` y la comparten el inbox y este panel. Una segunda copia
//    aquí pasaría todas las pruebas de hoy y se separaría de la otra al primer
//    arreglo — el síntoma (dos voces delante del cliente) se vería en una venta.
//
// 2) NO SE ABRE UNA SEGUNDA SUSCRIPCIÓN (I-4). `RealtimeClient.channel(topic)`
//    DEVUELVE el canal que ya existe cuando el topic se repite, y las
//    suscripciones `postgres_changes` viajan en el `join`: un `.on()` añadido
//    después del `subscribe` no recibe NADA, y sin error (medido en
//    realtime-js 2.111). Este panel escucha el `bus` que le pasa el embudo, que
//    reenvía los avisos de LA suscripción de la pantalla.
//
// 3) LA ANIMACIÓN NO MANDA (I-10). El fondo se funde por su cuenta, la carga de
//    datos arranca sin esperar a nadie, y `animarGenio` resuelve de inmediato
//    con `prefers-reduced-motion` o sin `element.animate`.
// ============================================================================
import { useCallback, useEffect, useRef, useState } from "react";
import { PanelRight, PanelRightClose, X } from "lucide-react";
import { Ficha360, type EdicionContacto } from "./Ficha360.tsx";
import { Hilo } from "./Hilo.tsx";
import {
  adjuntarYEnviar, cerrarConversacion, crearNota as rpcCrearNota, devolverAlBot, editarContacto,
  explicarError, leerConversacion, leerFicha, listarMensajes, listarNotas, listarRespuestasRapidas, marcarLeida,
  moverEtapa as rpcMoverEtapa, reclamarChat, tomarChat, urlDeMedia,
  instruirAlBot, CHAT_DE_OTRA_PERSONA,
} from "./datos.ts";
import { responderComoAsesor } from "./responder.ts";
import { animarGenio } from "./genio.ts";
import {
  nombreMostrado,
  type Contacto, type ContactoEmbudo, type Conversacion, type Etapa, type FichaCalificacion, type Mensaje, type Nota,
  type RespuestaRapida,
} from "./tipos.ts";

// ── T-LAT-7 · R-LAT-06 · LA FICHA SE PLIEGA Y SE RECUERDA ───────────────────
// 🔴 EL DAÑO: en un portátil (1440 px) el panel mide 960 y la ficha se lleva
// 340 fijos — el chat vive en 620 px por una ficha que casi siempre no se está
// mirando. Medido el 2-sep-2026.
// ⛔ UNA SOLA CLAVE, por navegador y NO por negocio: esto es la postura de
//    trabajo de una persona, no un ajuste del inquilino. Si fuera por tenant,
//    quien atiende dos negocios tendría que volver a plegarla en cada uno.
// ⚠️ Por defecto DESPLEGADA: el que no toque nada ve exactamente lo de siempre.
export const LLAVE_FICHA_PLEGADA = "vx-panel-ficha-plegada";
/** ⚠️ `localStorage` puede LANZAR (Safari privado, cookies bloqueadas, un
 *  `iframe` sin permiso), no solo devolver `null`. Sin el `try` el panel entero
 *  no monta y el asesor se queda sin chat por una preferencia de maquetación. */
export function leerFichaPlegada(): boolean {
  try { return localStorage.getItem(LLAVE_FICHA_PLEGADA) === "1"; } catch { return false; }
}
function guardarFichaPlegada(plegada: boolean) {
  try { localStorage.setItem(LLAVE_FICHA_PLEGADA, plegada ? "1" : "0"); } catch { /* sin storage: no pasa nada */ }
}

export interface PanelContactoProps {
  sb: any;
  tenantId: string;
  usuarioId: string | null;
  rol: "dueno" | "agente" | "victorix";
  contacto: ContactoEmbudo;
  etapas: Etapa[];
  /** Rectángulo de la tarjeta que se pulsó: de ahí nace el panel. `null` ⇒ sin animación. */
  desde: DOMRect | null;
  /** Los avisos en vivo REENVIADOS por el embudo. Este panel no se suscribe. */
  bus: EventTarget;
  onCerrar: () => void;
  /** ¿El canal en vivo ya estaba caído al abrir el panel? (R2-H1) */
  canalCaidoAlAbrir?: boolean;
  /** Parche para la tarjeta del embudo (apagar el aviso, cambiar nombre/título/etiquetas). */
  onContactoCambiado: (parche: Partial<ContactoEmbudo>) => void;
}

export function PanelContacto({
  sb, tenantId, usuarioId, contacto, etapas, desde, bus, onCerrar, onContactoCambiado, canalCaidoAlAbrir,
}: PanelContactoProps) {
  const convId = contacto.conversacion_id;

  const [conv, setConv] = useState<Conversacion | null>(null);
  const [mensajes, setMensajes] = useState<Mensaje[]>([]);
  const [notas, setNotas] = useState<Nota[]>([]);
  // T-CAL-2 · lo que el bot ya anotó del contacto (solo se muestra).
  const [ficha, setFicha] = useState<FichaCalificacion | null>(null);
  const [errorFicha, setErrorFicha] = useState<string | null>(null);
  const [respuestas, setRespuestas] = useState<RespuestaRapida[]>([]);
  const [cargando, setCargando] = useState(!!convId);
  const [error, setError] = useState<string | null>(null);
  const [fichaAbierta, setFichaAbierta] = useState(false);
  // T-LAT-7 · R-LAT-06. Se LEE al montar (no en un efecto): con un efecto, el
  // primer pintado saldría con la ficha desplegada y se vería el salto.
  const [fichaPlegada, setFichaPlegada] = useState<boolean>(leerFichaPlegada);

  const panelRef = useRef<HTMLDivElement>(null);
  // Las props que usan los efectos van por ref: si entraran en las dependencias,
  // el anfitrión re-renderizando volvería a cargar el hilo y a marcar leída.
  const cambioRef = useRef(onContactoCambiado);
  cambioRef.current = onContactoCambiado;
  const contactoRef = useRef(contacto);
  contactoRef.current = contacto;
  const noLeidosAlAbrir = useRef(contacto.no_leidos);
  // 🔴 H1 · T-PAN-5, un piso más arriba: `huboCaida` distingue «el canal VOLVIÓ»
  // de «se acaba de suscribir». Sin ese recuerdo, la primera suscripción
  // dispararía una recarga en cada montaje del panel.
  // R2-H1 del fiscal: si el lateral se ABRE con el canal ya caído, ese `conectado`
  // no es «primera suscripción»: es la vuelta. El embudo dice cómo está el canal.
  const huboCaida = useRef(!!canalCaidoAlAbrir);

  const nombre = nombreMostrado(contacto);

  // ── Carga, en el MISMO orden que el inbox (R-LAT-03) ──────────────────────
  useEffect(() => {
    if (!convId) { setCargando(false); return; }
    let vivo = true;
    setCargando(true);
    (async () => {
      try {
        const c = await leerConversacion(sb, convId);
        if (!vivo) return;
        setConv(c);
        const ms = await listarMensajes(sb, convId);
        if (!vivo) return;
        setMensajes(ms);
        // 🔴 ABRIR MARCA LEÍDA (I-7). Sin esto la tarjeta se queda en rojo
        // después de que la asesora ya leyó y contestó: el aviso deja de
        // significar «pide respuesta» y el tablero entero se vuelve ruido.
        if (noLeidosAlAbrir.current > 0) {
          await marcarLeida(sb, convId);
          if (!vivo) return;
          cambioRef.current({ no_leidos: 0 });
        }
        const ns = await listarNotas(sb, contactoRef.current.id);
        if (!vivo) return;
        setNotas(ns);
        const rr = await listarRespuestasRapidas(sb, tenantId);
        if (vivo) setRespuestas(rr);
      } catch (e) {
        if (vivo) setError(explicarError(e));
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, [sb, convId, tenantId]);

  // ── T-CAL-2 · la ficha de calificación ───────────────────────────────────
  // ⛔ I-94 · APARTE de la carga del hilo y con su propio `catch`: un fallo aquí
  // no puede caer en el `setError` de arriba ni cortar notas y respuestas. Va
  // por contacto, no por conversación: una tarjeta sin chat también tiene ficha.
  const contactoId = contacto.id;
  useEffect(() => {
    setFicha(null); setErrorFicha(null);
    let vivo = true;
    leerFicha(sb, tenantId, contactoId).then(
      (f) => { if (vivo) setFicha(f); },
      () => { if (vivo) setErrorFicha("No se pudo cargar lo que sabemos del cliente."); },
    );
    return () => { vivo = false; };
  }, [sb, tenantId, contactoId]);

  // ── En vivo, SIN suscripción propia (I-4) ─────────────────────────────────
  // Mismo tratamiento que `Inbox.tsx`, sobre los eventos que reenvía el embudo.
  useEffect(() => {
    if (!convId) return;
    const alMensaje = (ev: Event) => {
      const m = (ev as CustomEvent<Mensaje>).detail;
      if (!m || m.conversacion_id !== convId) return;
      setMensajes((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
    };
    const alEntrega = (ev: Event) => {
      const a = (ev as CustomEvent<{ mensaje_id: number; estado: any }>).detail;
      if (!a) return;
      setMensajes((prev) => {
        const i = prev.findIndex((m) => m.id === a.mensaje_id);
        // No es de este hilo: se devuelve el MISMO arreglo, no una copia. Un
        // arreglo nuevo re-renderiza el hilo entero por cada acuse del negocio.
        if (i === -1 || prev[i].entrega === a.estado) return prev;
        const copia = prev.slice();
        copia[i] = { ...copia[i], entrega: a.estado };
        return copia;
      });
    };
    const alMedia = (ev: Event) => {
      const a = (ev as CustomEvent<any>).detail;
      if (!a) return;
      setMensajes((prev) => {
        const i = prev.findIndex((m) => m.id === a.mensaje_id);
        if (i === -1) return prev;
        const { mensaje_id: _ignorado, ...campos } = a;
        const antes = prev[i].media;
        if (antes && (Object.keys(campos) as (keyof typeof campos)[])
          .every((k) => (antes as any)[k] === campos[k])) return prev;
        const copia = prev.slice();
        copia[i] = { ...copia[i], media: campos };
        return copia;
      });
    };
    const alConversacion = (ev: Event) => {
      const c = (ev as CustomEvent<Partial<Conversacion>>).detail;
      if (!c || c.id !== convId) return;
      setConv((prev) => (prev ? ({ ...prev, ...c } as Conversacion) : prev));
      // 🔴 I-7, LA MITAD QUE SE OLVIDA: lo que llega MIENTRAS el panel está
      // abierto ya lo está viendo la asesora. Si no se volviera a marcar, el
      // aviso de la tarjeta se reencendería con el chat delante.
      if ((c.no_leidos ?? 0) > 0) {
        marcarLeida(sb, convId).then(
          () => cambioRef.current({ no_leidos: 0 }),
          (e) => console.error("No se pudo marcar leída la conversación abierta", e),
        );
      }
    };
    // ── 🔴 H1 · EL HUECO DE UNA CAÍDA TAMBIÉN SE REPONE AQUÍ (I-7) ─────────
    // Medido el 1-sep sobre el panel publicado: el socket muere, la librería
    // reconecta sola a los ~81 s… y NO se hace una sola consulta al volver.
    // Realtime no repite lo del hueco. Con el lateral abierto eso significa un
    // hilo sin los mensajes que llegaron y una tarjeta encendida detrás del
    // fondo oscuro: la asesora contesta a una pregunta vieja creyendo que está
    // al día. Y la franja «Sin conexión» del embudo la tapa este panel.
    const reponer = async () => {
      try {
        const ms = await listarMensajes(sb, convId);
        setMensajes(ms);
        // Lo que llegó en el hueco ya lo está viendo: no reenciende el aviso.
        await marcarLeida(sb, convId);
        cambioRef.current({ no_leidos: 0 });
      } catch (e) {
        console.error("No se pudo reponer el hilo tras la caída del canal", e);
      }
    };
    const alEstado = (ev: Event) => {
      const e = (ev as CustomEvent<"conectado" | "caido">).detail;
      if (e === "caido") { huboCaida.current = true; return; }
      if (!huboCaida.current) return;      // primera suscripción: nada que reponer
      huboCaida.current = false;
      void reponer();
    };
    bus.addEventListener("estado", alEstado);
    bus.addEventListener("mensaje", alMensaje);
    bus.addEventListener("entrega", alEntrega);
    bus.addEventListener("media", alMedia);
    bus.addEventListener("conversacion", alConversacion);
    return () => {
      bus.removeEventListener("estado", alEstado);
      bus.removeEventListener("mensaje", alMensaje);
      bus.removeEventListener("entrega", alEntrega);
      bus.removeEventListener("media", alMedia);
      bus.removeEventListener("conversacion", alConversacion);
    };
  }, [bus, convId, sb]);

  // ── Abrir y cerrar con el efecto genio (R-LAT-01 · I-10) ──────────────────
  /** La tarjeta de ESTE contacto, si sigue en el tablero. De ella salen el borde
   *  y la sombra con que arranca la lámina, y es la que da el temblorcito de
   *  T-LAT-7. ⚠️ Puede no estar (se filtró, cambió de etapa, se salió de la
   *  vista) y eso es un camino LEGÍTIMO: sin tarjeta no hay temblor y ya. */
  const tarjetaViva = useCallback((): HTMLElement | null => {
    if (typeof document === "undefined") return null;
    return Array.from(document.querySelectorAll<HTMLElement>("[data-contacto]"))
      .find((n) => n.dataset.contacto === contactoRef.current.id) ?? null;
  }, []);

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    let vivo = true;
    // ⚠️ La carga de datos NO espera a esto (arranca en su propio efecto): la
    // animación acompaña, no manda.
    void animarGenio(el, desde, "abrir", tarjetaViva()).then(() => {
      if (!vivo) return;
      const caja = el.querySelector<HTMLTextAreaElement>('textarea[aria-label="Mensaje para el cliente"]');
      caja?.focus();
    });
    return () => { vivo = false; };
    // Solo al montar: `desde` es el rectángulo del clic y no cambia en vida.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Una sola vez, pase lo que pase con la animación: dos `onCerrar` desmontarían
  // dos veces y el segundo dejaría al embudo abriendo un panel fantasma.
  const cerrandoRef = useRef(false);
  const cerrarPanel = useCallback(() => {
    if (cerrandoRef.current) return;
    cerrandoRef.current = true;
    const el = panelRef.current;
    // ⚠️ `desde` es el rectángulo del CLIC, y para cuando se cierra la tarjeta
    // casi nunca sigue ahí: un mensaje nuevo la sube al frente de su columna, o
    // el asesor movió la rueda del ratón. Cerrar contra esa foto vieja mete el
    // panel en un hueco vacío — se ve tan mal como no animar. Si la tarjeta
    // sigue en pantalla se le pide su sitio de AHORA; si ya no está (se filtró,
    // cambió de etapa), la foto vieja es mejor que nada.
    const viva = tarjetaViva();
    const hacia = viva?.getBoundingClientRect() ?? desde;
    const fin = el ? animarGenio(el, hacia, "cerrar", viva) : Promise.resolve();
    void fin.then(() => onCerrar());
  }, [desde, onCerrar, tarjetaViva]);

  // 🔴 H3 · `Esc` NO CIERRA A CIEGAS. Tres formas de perder trabajo con una sola
  // tecla: (1) tres renglones escritos en la caja y un Esc por reflejo cierran
  // el panel y el borrador se va con el `Hilo` desmontado; (2) escribiendo una
  // etiqueta, la ficha limpia el campo Y el mismo keydown sube a `window` y
  // cierra todo; (3) bajo `xl`, con la capa «Ficha» encima, Esc debería cerrar
  // LA CAPA, no el panel entero. Se cierra de fuera hacia dentro.
  useEffect(() => {
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      const etiqueta = t?.tagName?.toLowerCase();
      if (etiqueta === "input" || etiqueta === "textarea" || etiqueta === "select" || t?.isContentEditable) return;
      if (fichaAbierta) { setFichaAbierta(false); return; }
      cerrarPanel();
    };
    window.addEventListener("keydown", alTeclado);
    return () => window.removeEventListener("keydown", alTeclado);
  }, [cerrarPanel, fichaAbierta]);

  // ── Acciones ──────────────────────────────────────────────────────────────
  const firmarMedia = useCallback((ruta: string) => urlDeMedia(sb, ruta), [sb]);

  const parchar = (p: Partial<Conversacion>) =>
    setConv((c) => (c ? ({ ...c, ...p } as Conversacion) : c));

  const conError = (fn: () => Promise<void>) => async () => {
    try { await fn(); } catch (e) { setError(explicarError(e)); }
  };

  async function enviar(texto: string) {
    if (!conv) return;
    // I-6: LA MISMA función que usa el inbox. No hay una segunda copia.
    await responderComoAsesor(sb, conv, texto, usuarioId, parchar);
    try {
      setMensajes(await listarMensajes(sb, conv.id));
    } catch (e: any) {
      throw Object.assign(new Error(explicarError(e)), { mensaje: explicarError(e) });
    }
  }

  /** T-ETQ-4 (R-ETQ-01) desde el lateral: lo guardado se ve en la tarjeta al instante. */
  async function editarContactoActivo(c: EdicionContacto) {
    try { await editarContacto(sb, contactoRef.current.id, c); }
    catch (e) { throw Object.assign(new Error(explicarError(e)), { mensaje: explicarError(e) }); }
    cambioRef.current(c);
    setConv((x) => (x && x.contacto ? { ...x, contacto: { ...x.contacto, ...c } } : x));
  }

  function cambiarFichaPlegada(v: boolean) { setFichaPlegada(v); guardarFichaPlegada(v); }

  /** El botón que pliega la ficha. ⛔ VA SOLO DESDE `xl` Y ESO NO ES UN OLVIDO:
   *  por debajo de `xl` la ficha ya NO le roba ni un píxel al hilo (es una capa
   *  que se abre desde el nombre del contacto), así que ahí no hay nada que
   *  plegar. Se resuelve con CSS y no con `matchMedia` a propósito: un umbral
   *  escrito en JS y otro en las clases se separan al primer retoque, y el
   *  síntoma sería un botón que no hace nada. */
  const botonPlegar = (
    <button
      onClick={() => cambiarFichaPlegada(!fichaPlegada)}
      aria-pressed={fichaPlegada}
      aria-label={fichaPlegada ? "Mostrar la ficha del contacto" : "Ocultar la ficha del contacto"}
      title={fichaPlegada ? "Mostrar la ficha" : "Ocultar la ficha y ver el chat más grande"}
      data-plegar-ficha={fichaPlegada ? "plegada" : "desplegada"}
      className="hidden xl:grid shrink-0 h-8 w-8 place-items-center rounded-lg text-tinta-2
        hover:text-tinta hover:bg-tenue active:scale-90 transition-transform cursor-pointer"
    >
      {fichaPlegada ? <PanelRight size={17} strokeWidth={1.75} /> : <PanelRightClose size={17} strokeWidth={1.75} />}
    </button>
  );

  const fichaDe = (conCerrar: boolean) => (
    <Ficha360
      contacto={(conv?.contacto ?? contacto) as Contacto}
      etapas={etapas}
      notas={notas}
      ficha={ficha}
      errorFicha={errorFicha}
      // T-CAL-4 · la temperatura que ya trajo la tarjeta (sin RPC nueva).
      temperatura={contacto}
      // T-ANU-10 · el anuncio que ya trajo la tarjeta (sin RPC nueva).
      anuncio={contacto}
      onCerrar={conCerrar ? () => setFichaAbierta(false) : undefined}
      onMoverEtapa={async (etapaId) => {
        await rpcMoverEtapa(sb, contactoRef.current.id, etapaId);
        // T-CAL-19 · mover desde la ficha también deja el candado (mover_etapa con JWT, 0069).
        cambioRef.current({ etapa_id: etapaId, etapa_puesta_por: "equipo" });
        setConv((x) => (x && x.contacto ? { ...x, contacto: { ...x.contacto, etapa_id: etapaId } } : x));
      }}
      onCrearNota={async (texto) => {
        await rpcCrearNota(sb, contactoRef.current.id, texto);
        setNotas(await listarNotas(sb, contactoRef.current.id));
      }}
      onEditarContacto={editarContactoActivo}
    />
  );

  return (
    <div className="fixed inset-0 z-40 flex" role="dialog" aria-modal="true"
      aria-label={`Conversación de ${nombre}`}>
      {/* El fondo se funde por su cuenta y NO espera al panel (I-10). Lo funde
          `animarGenio` por el `data-genio` —a la vez que la lámina y también AL
          CERRAR, que antes desaparecía de golpe—; la clase `entra` no servía
          aquí porque además lo DESPLAZABA, y un fondo que se desliza delata la
          animación en vez de acompañarla. */}
      <button
        data-genio="fondo"
        className="flex-1 bg-black/30 cursor-default"
        aria-label="Cerrar la conversación"
        onClick={cerrarPanel}
      />
      {/* La X flota SOBRE EL FONDO, a la izquierda del panel (como una hoja de
          macOS): dentro del panel se montaba sobre los chips de la cabecera del
          hilo. En pantallas chicas (< lg) no cabe fuera: ahí cierra la flecha
          «volver» del propio hilo (`onVolver`). */}
      <button onClick={cerrarPanel} aria-label="Cerrar el panel" data-genio="x"
        className="hidden lg:grid absolute top-3 right-[calc(min(94vw,960px)+0.75rem)] z-50 h-9 w-9
          place-items-center rounded-full bg-superficie text-tinta-2 shadow-[var(--shadow-flotante)]
          hover:text-tinta active:scale-90 transition-transform cursor-pointer">
        <X size={18} />
      </button>
      <div
        ref={panelRef}
        // ⛔⛔ DÓNDE ESTÁ DE VERDAD EL TIRANTE DE ESTA PANTALLA (medido en Chromium
        // por el fiscal el 2-sep-2026, y CORRIGE lo que decía aquí antes):
        //   · `.panel` (este div) es hijo de un flex de **FILA** (`fixed inset-0
        //     … flex`, sin `flex-col`), así que en él y en sus hijos el eje
        //     principal es el HORIZONTAL ⇒ la regla de `min-height:auto` **no
        //     aplica** y estos `min-h-0` valen exactamente CERO.
        //   · Lo que de verdad acota la altura es el **`overflow-y-auto` del log
        //     del hilo** (`Hilo.tsx`, el `div role="log"`): por regla de flexbox,
        //     un elemento con `overflow` distinto de `visible` tiene tamaño mínimo
        //     automático 0. **Ése es el tirante, y ése es el sitio a vigilar.**
        //   · Medido: quitar `min-h-0` de aquí deja el log en clientH 754 /
        //     scrollH 4800, IDÉNTICO a no quitarlo. Quitarle el `overflow-y-auto`
        //     al log lo deja en 4800/4800 y la pantalla se congela.
        // ⚠️ Se CONSERVAN por cinturón —si mañana alguien mete un `flex-col` en el
        //    diálogo, empiezan a valer— pero **nadie debe contarlos como la
        //    protección**: la que muerde es `puedeP` de `tools/vigila-pantalla.mjs`,
        //    que comprueba que el hilo de verdad sube y baja. Y sí: en el Inbox el
        //    `min-h-0` del 1-sep SÍ era el tirante, porque allí el contenedor es
        //    una COLUMNA. La misma clase, dos valores distintos según el padre —
        //    por eso esto se escribe y no se deduce.
        className="relative h-full min-h-0 w-[min(94vw,960px)] shrink-0 flex bg-superficie
          rounded-l-2xl overflow-hidden shadow-[var(--shadow-flotante)]"
      >

        <div className="flex-1 min-w-0 min-h-0 flex flex-col">
          {convId ? (
            <Hilo
              onVolver={cerrarPanel}
              conversacion={conv}
              mensajes={mensajes}
              cargando={cargando}
              respuestas={respuestas}
              firmarMedia={firmarMedia}
              onEnviar={enviar}
              onAdjuntar={conv ? (async (archivo) => {
                await adjuntarYEnviar(sb, {
                  convId: conv.id, tenantId: conv.tenant_id,
                  archivo, autorId: usuarioId ?? null,
                });
                setMensajes(await listarMensajes(sb, conv.id));
              }) : undefined}
              onTomar={usuarioId && conv ? conError(async () => {
                await tomarChat(sb, conv.id, usuarioId);
                parchar({ estado: "humano", asignada_a: usuarioId });
                setMensajes(await listarMensajes(sb, conv.id));
              }) : undefined}
              onReclamar={usuarioId && conv ? conError(async () => {
                await reclamarChat(sb, conv.id, usuarioId);
                parchar({ asignada_a: usuarioId });
              }) : undefined}
              onDevolver={conError(async () => {
                if (!conv) return;
                await devolverAlBot(sb, conv.id);
                parchar({ estado: "bot" });
              })}
              onCerrar={conError(async () => {
                if (!conv) return;
                await cerrarConversacion(sb, conv.id);
                parchar({ estado: "cerrada" });
              })}
              // ⭐ T-HIB-1 · la misma caja «Instrucción al bot» que en la Bandeja
              // (25-sep: Cristhian abrió el chat desde el Embudo y no la vio).
              // Si el chat lo tiene OTRA persona: aviso, no caja (decisión A; la
              // 0066 lo rechaza igual).
              avisoInstruccion={conv?.asignada_a && conv.asignada_a !== usuarioId
                ? CHAT_DE_OTRA_PERSONA : undefined}
              onInstruir={!conv || (conv.asignada_a && conv.asignada_a !== usuarioId) ? undefined : async (texto) => {
                try { await instruirAlBot(sb, conv.id, texto); }
                catch (e) { throw Object.assign(new Error(explicarError(e)), { mensaje: explicarError(e) }); }
                parchar({ estado: "bot", asignada_a: null, motivo_humano: null });
                setMensajes(await listarMensajes(sb, conv.id));
              }}
              onVerFicha={() => setFichaAbierta(true)}
              accionFicha={botonPlegar}
            />
          ) : (
            // Un contacto del embudo puede no tener conversación abierta: se
            // enseña su ficha, y donde iría el hilo, el porqué — sin caja de
            // escribir, que no tendría a dónde mandar nada.
            <div className="flex-1 min-h-0 flex flex-col">
              <header className="shrink-0 flex items-center gap-2 px-3 sm:px-4 py-2.5 border-b border-borde-suave">
                <span className="text-[15px] font-semibold truncate">{nombre}</span>
                <span className="ml-auto">{botonPlegar}</span>
              </header>
              <div className="flex-1 min-h-0 grid place-items-center px-6 text-center">
                <p className="text-[13.5px] text-tinta-2 max-w-[280px]">
                  Este contacto no tiene una conversación abierta.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Ficha fija desde `xl` —y PLEGABLE desde T-LAT-7—; por debajo, capa que
            se abre desde la cabecera.
            ⚠️ Al plegar, el hilo se queda con TODO el ancho. Lo vigila
            `tools/vigila-pantalla.mjs` con el caso de la ficha plegada — y lo que
            allí muerde es que el hilo SUBA Y BAJE de verdad, no la cadena de
            clases (ver el ⛔⛔ del panel, arriba: aquí `min-h-0` es un no-op). */}
        {!fichaPlegada && (
          <div className="hidden xl:block w-[340px] shrink-0 min-h-0">{fichaDe(false)}</div>
        )}

        {error && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 entra">
            <div role="alert" className="bg-peligro text-white text-[13px] px-4 py-2.5 rounded-lg
              shadow-[var(--shadow-flotante)] max-w-[90vw]">
              {error}
              <button onClick={() => setError(null)} className="ml-3 underline cursor-pointer">cerrar</button>
            </div>
          </div>
        )}
      </div>

      {fichaAbierta && (
        <div className="xl:hidden fixed inset-0 z-50 flex" role="dialog" aria-modal="true"
          aria-label="Ficha del contacto">
          <button className="flex-1 bg-black/40 cursor-pointer" aria-label="Cerrar ficha"
            onClick={() => setFichaAbierta(false)} />
          <div className="entra w-[86%] max-w-[340px] shadow-[var(--shadow-flotante)] relative">
            {fichaDe(true)}
          </div>
        </div>
      )}
    </div>
  );
}
